import { useToast } from '@shared/hooks/use-toast';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PackApiAdapter } from '../adapters/PackApiAdapter';
import { CreatePackRequest } from '../models/Pack';
import { PackService } from '../services/PackService';

interface UseCreatePackOptions {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
}

export const useCreatePack = (options: UseCreatePackOptions = {}) => {
    const queryClient = useQueryClient();
    const httpClient = createHttpClient(apiClientWH);
    const apiAdapter = new PackApiAdapter(httpClient);
    const packService = new PackService(apiAdapter);
    const { toast } = useToast();

    const mutation = useMutation({
        mutationFn: (data: CreatePackRequest) => packService.createPack(data),
        onSuccess: (data) => {
            // 🎯 Invalidar todas las queries relacionadas con packs
            queryClient.invalidateQueries({ queryKey: ['packs'] });
            queryClient.invalidateQueries({ queryKey: ['packs-simple'] });

            // 🎯 Opcional: Agregar el nuevo pack al cache
            queryClient.setQueryData(['pack', data.id], data);

            // 🎯 Mostrar mensaje de éxito
            toast({
                title: "Pack creado",
                description: `El pack se ha creado correctamente.`,
                variant: "default",
            });

            // 🎯 Callback personalizado
            options.onSuccess?.();
        },
        onError: (error: Error) => {
            console.log(error);
            // 🎯 Mostrar mensaje de error
            toast({
                title: "Error al crear pack",
                description: error.message || "Ha ocurrido un error inesperado.",
                variant: "destructive",
            });

            // 🎯 Callback personalizado de error  
            options.onError?.(error);
        },
    });

    return {
        createPack: mutation.mutate,
        createPackAsync: mutation.mutateAsync,
        isCreating: mutation.isPending,
        error: mutation.error,
        isSuccess: mutation.isSuccess,
        reset: mutation.reset,
    };
}; 